// THE # PICKER — type # in a register prose field and the referenceable rows offer
// themselves: refs with their names, filtered as you keep typing, Enter/click inserts.
//
// WHAT IS INSERTED IS THE PLAIN REF (DEL-02), never a markup token. The record's prose stays
// canonical text — every consumer (SOW render, workspace projections, translations, the
// derived reference scan in utils/proseReferences.ts, the chips refLink.ts recovers) already
// reads bare refs, and a special #-syntax would put structure into prose that half the
// product would then have to unlearn. The # is only the KEY that summons the picker; it does
// not survive into the text.
//
// Attached to the HOST'S textarea by ref, controlled or uncontrolled alike: the insert goes
// through the native value setter + an InputEvent, which React's onChange sees like typing.
// The dropdown is position:fixed, measured off the textarea when the token opens — an
// accordion body or a clipped sheet between here and the viewport cannot cut it.
import { useCallback, useEffect, useRef, useState } from 'react';

import { useT } from '../i18n/index.js';
import './RefSuggest.de.js';
import './RefSuggest.fr.js';

export interface RefOption {
  ref: string;
  label: string;
}

/** The token under the caret: the run from a word-starting '#' up to the caret, or null. */
function hashTokenAt(value: string, caret: number): { start: number; query: string } | null {
  const head = value.slice(0, caret);
  const m = /#([A-Za-z0-9._-]{0,32})$/.exec(head);
  if (!m) return null;
  const start = caret - m[1].length - 1;
  if (start > 0 && /[A-Za-z0-9#]/.test(head[start - 1])) return null;   // mid-word # is not an ask
  return { start, query: m[1] };
}

export function RefSuggest({ taRef, options }: {
  taRef: React.RefObject<HTMLTextAreaElement | null>;
  options: RefOption[];
}) {
  const t = useT();
  const [state, setState] = useState<{ query: string; index: number; x: number; y: number; w: number } | null>(null);

  const matches = state === null
    ? []
    : options
        .filter((o) => {
          const q = state.query.toLowerCase();
          return !q || o.ref.toLowerCase().includes(q) || o.label.toLowerCase().includes(q);
        })
        .slice(0, 8);
  /** The keydown listeners read the CURRENT match count through this, never a stale closure. */
  const matchesRef = useRef<RefOption[]>(matches);
  matchesRef.current = matches;

  const pick = useCallback((option: RefOption) => {
    const el = taRef.current;
    if (!el) return;
    // The token is re-read at pick time: the caret may have moved (a click, a selection)
    // since the state last saw it, and splicing against a stale start corrupts the prose.
    const caret = el.selectionStart ?? el.value.length;
    const token = hashTokenAt(el.value, caret);
    if (!token) { setState(null); return; }
    const next = `${el.value.slice(0, token.start)}${option.ref} ${el.value.slice(caret)}`;
    // Native setter + InputEvent: a controlled React textarea sees this exactly like typing.
    const setter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value')?.set;
    setter?.call(el, next);
    el.dispatchEvent(new Event('input', { bubbles: true }));
    const at = token.start + option.ref.length + 1;
    el.setSelectionRange(at, at);
    el.focus();
    setState(null);
  }, [taRef]);
  const pickRef = useRef(pick);
  pickRef.current = pick;

  // REBOUND ON EVERY RENDER on purpose: hosts remount their textarea under a changed `key`
  // (the detail modal re-keys on every save) while the ref OBJECT stays identical — a
  // dependency array would never fire again and the picker would quietly die after the first
  // save. Removing and re-adding four listeners per render is nothing.
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    const read = () => {
      const token = hashTokenAt(el.value, el.selectionStart ?? el.value.length);
      if (!token) { setState((cur) => (cur === null ? cur : null)); return; }
      const r = el.getBoundingClientRect();
      setState((cur) => ({
        query: token.query,
        index: cur && cur.query === token.query ? cur.index : 0,
        x: Math.max(8, Math.min(r.left, window.innerWidth - r.width - 8)),
        y: r.bottom + 4,
        w: r.width,
      }));
    };
    const onKeyDown = (e: KeyboardEvent) => {
      // The picker owns keys only while it is VISIBLE — open with zero matches, the field
      // keeps every key (swallowing arrows against an empty dropdown froze the caret).
      const n = matchesRef.current.length;
      if (!n) return;
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setState(null); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setState((c) => (c ? { ...c, index: Math.min(c.index + 1, n - 1) } : c)); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setState((c) => (c ? { ...c, index: Math.max(0, c.index - 1) } : c)); return; }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        e.stopPropagation();
        setState((c) => {
          if (c) pickRef.current(matchesRef.current[Math.min(c.index, n - 1)]);
          return null;
        });
      }
    };
    const onBlur = () => setTimeout(() => setState(null), 150);   // a click on an option lands first
    el.addEventListener('input', read);
    el.addEventListener('click', read);
    el.addEventListener('keydown', onKeyDown);
    el.addEventListener('blur', onBlur);
    return () => {
      el.removeEventListener('input', read);
      el.removeEventListener('click', read);
      el.removeEventListener('keydown', onKeyDown);
      el.removeEventListener('blur', onBlur);
    };
  });

  if (state === null || !matches.length) return null;
  const active = Math.min(state.index, matches.length - 1);
  return (
    <div
      className="refsuggest"
      role="listbox"
      aria-label={t('Insert a reference')}
      style={{ left: state.x, top: state.y, width: Math.max(280, state.w) }}
    >
      {matches.map((o, i) => (
        <button
          type="button"
          key={o.ref}
          role="option"
          aria-selected={i === active}
          className={`refsuggest-row${i === active ? ' on' : ''}`}
          onMouseDown={(e) => { e.preventDefault(); pick(o); }}
        >
          <span className="src-ref">{o.ref}</span>
          <span className="t">{o.label}</span>
        </button>
      ))}
    </div>
  );
}

export default RefSuggest;
